import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  Fragment,
} from 'react';
import Field from './Field';

/**
 * Ticket code input component
 * @param {Object} [props]
 * @param {Function} [props.onChange] - Callback method when value is changed
 * @param {number} [props.length=9] - Number of characters in the code
 * @param {number} [props.groupSize=3] - Group size for separator (e.g., every 3 chars)
 * @component
 */
export default forwardRef(({ onChange = () => {}, length = 9, groupSize = 3, ...restProps }, ref) => {
  const [code, setCode] = useState('');
  const [inputFocued, setInputFocued] = useState(false);
  const input = useRef();

  useImperativeHandle(ref, () => ({ clear: () => setCode('') }));

  useEffect(() => {
    onChange(
      code
        .split('')
        .map((char, index) =>
          index !== 0 && groupSize && index % groupSize === 0 ? `-${char}` : char
        )
        .join('')
    );
  }, [code, onChange, groupSize]);

  const fieldValue = place => code.split('')[place - 1] || '';

  const handleFiledClick = index => () => {
    setCode(code.substring(0, index));
    input.current.focus();
  };

  const handleChange = ({ target: { value } }) =>
    setCode(
      value
        .toUpperCase()
        .replace(/[^a-z0-9]/gi, '')
        .substring(0, length)
    );

  const handleKeyUp = ({ target }) =>
    target.setSelectionRange(target.value.length, target.value.length);

  return (
    <div className="TicketCodeInput" {...restProps}>
      {/* No maxLength here on purpose: the browser applies it to the raw text,
          so pasting a grouped code like "Q2R-HG7-PX9" gets cut to 9 characters
          BEFORE the separators are stripped, silently losing the last two.
          handleChange already truncates to `length` after stripping. */}
      <input
        ref={input}
        className="TicketCodeInput-Input"
        type="text"
        value={code}
        onFocus={() => setInputFocued(true)}
        onBlur={() => setInputFocued(false)}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
      />

      {[...Array(length)].map((_, index) => (
        <Fragment key={index}>
          {index !== 0 && groupSize && index % groupSize === 0 && (
            <span className="TicketCodeInput-separator" />
          )}

          <Field
            active={inputFocued && code.length === index}
            onClick={handleFiledClick(index)}
          >
            {fieldValue(index + 1)}
          </Field>
        </Fragment>
      ))}
    </div>
  );
});
